import styled, { keyframes, css } from 'styled-components'
import { media } from '../../../../utils/mediaQuery'

const fillProgress = keyframes`
  from {
    width: 0%;
  }
`

export const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xlarge};
  padding-top: ${({ theme }) => theme.spacing.huge};
`

export const ModalTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.large} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
`

export const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xlarge};
  margin-bottom: ${({ theme }) => theme.spacing.xlarge};

  ${media.tabletDown} {
    grid-template-columns: 1fr;
  }
`

export const RadarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

export const OverallScoreSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`

export const OverallScoreTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const OverallScoreValue = styled.div<{ $color: string }>`
  font-size: 38px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ $color }) => $color};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const OverallScoreDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

export const InfoNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
  margin-top: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  margin: ${({ theme }) => theme.spacing.large} 0;
`

export const BreakdownSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const BreakdownTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const BreakdownItem = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`

export const BreakdownItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const BreakdownItemTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const BreakdownItemSubtitle = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-left: ${({ theme }) => theme.spacing.small};
`

export const BreakdownItemScore = styled.div<{ $color: string }>`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $color }) => $color};
`

export const ProgressBarContainer = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const ProgressBarFill = styled.div<{
  $percentage: number
  $color: string
  $animate: boolean
  $animationDelay: number
}>`
  height: 100%;
  width: ${({ $percentage }) => $percentage}%;
  background: ${({ $color }) => $color};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  transition: width 0.3s ease-out;

  ${({ $animate, $animationDelay }) =>
    $animate &&
    css`
      animation: ${fillProgress} 1s ease-out ${$animationDelay}ms forwards;
    `}
`

export const BreakdownItemDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`

export const MethodologySection = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.large};
`

export const MethodologyTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const MethodologySubsection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  &:last-child {
    margin-bottom: 0;
  }
`

export const MethodologySubtitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.xsmall} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const MethodologyList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing.large};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.6;

  li {
    margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  }
`
